import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Badge, Button, Card, CopyBox, Modal, TextField } from './ui';

/**
 * Two-step sign-in in "My profile": scan a QR code with an authenticator app, type the first code,
 * save the backup codes. Afterwards: new backup codes, or turn it off (needs the password).
 */
export function TwoFactorCard({ required = false }: { required?: boolean }) {
  const { reloadMe } = useAuth();
  const qc = useQueryClient();
  const state = useQuery({ queryKey: ['2fa'], queryFn: () => api.get<{ enabled: boolean; backupCodesLeft: number }>('/auth/2fa') });
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [askPassword, setAskPassword] = useState<'disable' | 'codes' | null>(null);
  const refresh = async () => { await qc.invalidateQueries({ queryKey: ['2fa'] }); await reloadMe(); };

  const start = async () => {
    try { setSetup(await api.post<{ secret: string; otpauthUrl: string }>('/auth/2fa/setup')); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Please try again.'); }
  };

  return (
    <Card className="p-6">
      <h2 className="flex items-center gap-2 font-semibold"><ShieldCheck className="size-5 text-brand-600" />Two-step sign-in {state.data?.enabled && <Badge tone="green">On</Badge>}</h2>
      {!state.data ? null : state.data.enabled ? (
        <>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            Signing in needs your password <b>and</b> a code from your phone, so a stolen password alone isn’t enough.
            You have <b>{state.data.backupCodesLeft}</b> unused backup code{state.data.backupCodesLeft === 1 ? '' : 's'}.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => setAskPassword('codes')}>New backup codes</Button>
            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setAskPassword('disable')}>Turn off</Button>
          </div>
        </>
      ) : (
        <>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            {required ? 'Your company asks Admins to use this. ' : ''}Protect your account with a code from your phone when you sign in. You need a free app such as Google Authenticator or Microsoft Authenticator.
          </p>
          <Button className="mt-4" onClick={start}>Turn on two-step sign-in</Button>
        </>
      )}
      {setup && <SetupDialog setup={setup} onClose={() => setSetup(null)} onDone={async (c) => { setSetup(null); setCodes(c); await refresh(); }} />}
      {codes && <BackupCodes codes={codes} onClose={() => setCodes(null)} />}
      {askPassword && <PasswordDialog mode={askPassword} onClose={() => setAskPassword(null)} onDone={async (c) => { setAskPassword(null); if (c) setCodes(c); await refresh(); }} />}
    </Card>
  );
}

function SetupDialog({ setup, onClose, onDone }: { setup: { secret: string; otpauthUrl: string }; onClose: () => void; onDone: (codes: string[]) => void }) {
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { void import('qrcode').then((m) => m.toDataURL(setup.otpauthUrl, { width: 240, margin: 1 }).then(setQr)); }, [setup.otpauthUrl]);
  const confirm = async () => {
    setBusy(true); setError(null);
    try { onDone((await api.post<{ backupCodes: string[] }>('/auth/2fa/enable', { code })).backupCodes); }
    catch (e) { setError(e instanceof Error ? e.message : 'Please try again.'); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Turn on two-step sign-in"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={confirm} loading={busy} disabled={code.replace(/\D/g, '').length !== 6}>Turn on</Button></>}>
      <ol className="space-y-4 text-sm">
        <li><b>1.</b> On your phone, open Google Authenticator or Microsoft Authenticator (free in the Play Store) and tap <b>+</b> → <b>Scan a QR code</b>.</li>
        <li className="flex justify-center">{qr ? <img src={qr} alt="QR code for your authenticator app" className="size-48 rounded-lg ring-1 ring-slate-200" /> : <div className="size-48 animate-pulse rounded-lg bg-slate-100" />}</li>
        <li className="text-slate-500">Can’t scan? Choose “Enter a setup key” in the app and type this key:<CopyBox label="" value={setup.secret} /></li>
        <li><b>2.</b> Type the 6-digit code the app shows for AI CRM:
          <TextField label="" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" autoFocus
            className="mt-2 text-center text-xl tracking-[0.4em]" placeholder="123456" error={error ?? undefined} />
        </li>
      </ol>
    </Modal>
  );
}

function BackupCodes({ codes, onClose }: { codes: string[]; onClose: () => void }) {
  const text = codes.join('\n');
  return (
    <Modal open onClose={onClose} title="Save your backup codes" footer={<Button onClick={onClose}>I’ve saved them</Button>}>
      <p className="text-sm text-slate-600 dark:text-slate-400">If you lose your phone, each of these codes lets you sign in once. Write them down or save them somewhere safe — they won’t be shown again.</p>
      <div className="my-4 grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-4 font-mono text-lg dark:bg-slate-800">{codes.map((c) => <span key={c}>{c}</span>)}</div>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" onClick={() => { void navigator.clipboard.writeText(text); toast.success('Copied.'); }}>Copy</Button>
        <Button variant="secondary" size="sm" onClick={() => { const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([`AI CRM backup codes\n\n${text}\n`], { type: 'text/plain' })); a.download = 'ai-crm-backup-codes.txt'; a.click(); }}>Download</Button>
      </div>
    </Modal>
  );
}

function PasswordDialog({ mode, onClose, onDone }: { mode: 'disable' | 'codes'; onClose: () => void; onDone: (codes?: string[]) => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true); setError(null);
    try {
      if (mode === 'disable') { await api.post('/auth/2fa/disable', { password }); toast.success('Two-step sign-in is off.'); onDone(); }
      else onDone((await api.post<{ backupCodes: string[] }>('/auth/2fa/backup-codes', { password })).backupCodes);
    } catch (e) { setError(e instanceof Error ? e.message : 'Please try again.'); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={mode === 'disable' ? 'Turn off two-step sign-in?' : 'Get new backup codes'}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button variant={mode === 'disable' ? 'danger' : 'primary'} loading={busy} disabled={!password} onClick={go}>{mode === 'disable' ? 'Turn off' : 'Get new codes'}</Button></>}>
      <p className="mb-3 text-sm text-slate-600 dark:text-slate-400">{mode === 'disable' ? 'Your account will be protected by your password only.' : 'Your old backup codes will stop working.'}</p>
      <TextField label="Your password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} error={error ?? undefined} autoFocus />
    </Modal>
  );
}
